import { ObjectPKInput, ObjectSKInput } from "../aws/DynamoDbApiTypes";
import { AbstractOrderLineItem, AbstractOrderLineItemFields, abstractOrderLineItemSchema } from "./AbstractOrderLineItem";
import { DynamoKey } from "./BaseDynamoClass";
import { StripUnderscore, UnderscoreKeys } from "../util/Types";
import { BasicError } from "./BasicError";
import { OrderBase, OrderPKInput } from "./OrderBase";
import z from "zod";
import { OrderItemStatus } from "./Enums";

export const orderItemUISchema = abstractOrderLineItemSchema.extend({
  _orderId: z.string().nonempty(),
  _orderItemId: z.string().nonempty(),
  _exteriorKey: z.string().optional(),
  _interiorKey: z.string().optional(),
  _unitPrice: z.string().nonempty()
});

export const orderItemUIProcessedSchema = orderItemUISchema.extend({
  _exteriorKey: z.string().nonempty(),
  _interiorKey: z.string().nonempty()
});

export type OrderItemUIFields = AbstractOrderLineItemFields & StripUnderscore<z.infer<typeof orderItemUISchema>>;
export class OrderItemUI<TFields extends OrderItemUIFields = OrderItemUIFields> extends AbstractOrderLineItem<TFields> {
	_orderId!: string;
	_orderItemId!: string;
	_exteriorKey?: string | undefined;
	_interiorKey?: string | undefined;
    override _className: string = "OrderItem";
	override _deserializeClassName: string = "OrderItemUI";

	constructor(props: Partial<TFields>) {
		super(props);
	}
	override getFieldList(): (keyof UnderscoreKeys<TFields>)[] {
		return [...super.getFieldList(), "_orderId", "_orderItemId", "_exteriorKey", "_interiorKey"];
	}
	override validateSchema(): void {
		switch(this.status){
			case OrderItemStatus.PROCESSED:
				orderItemUIProcessedSchema.parse(this);
				return;
			case OrderItemStatus.CREATED:
			default:
				orderItemUISchema.parse(this);
				return;
		}
	}
	getPKPrefix(): string {
		return "ORDER#";
	}
	getSKPrefix(props: object | undefined): string {
		return "ORDERITEM#";
	}
	getPK(): string {
		return this.getPKPrefix() + this.orderId;
	}
	getSK(): string {
		return this.getSKPrefix(this) + this.orderItemId;
	}
	getParentKey(): DynamoKey | undefined {
		if (!this.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return OrderBase.generateKey({ orderId: this.orderId });
	}
	getParentKeyInput(): ObjectPKInput | undefined {
		if (!this.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return { orderId: this.orderId };
	}
	getParentTableName(): string | undefined {
		return undefined;
	}
	generateKey(input: any): DynamoKey {
		if (!input.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		if (!input.orderItemId) throw new BasicError("Missing orderItemId", "Missing orderId");
		return {
			PK: this.getPKPrefix + input.orderId,
			SK: this.getSKPrefix(input) + input.orderItemId,
		};
	}
	generatePK(input: OrderPKInput): string {
		if (!input.orderId) throw new BasicError("Missing orderId", "Missing orderId");
		return this.getPKPrefix() + input.orderId;
	}
	generateGSIPK(index: number, key: ObjectPKInput): string {
		switch (index) {
			case 1:
			case 2:
			default:
				return "USER#" + (!!key && key.userId ? key.userId : "");
		}
	}
	generateGSISK(index: number, key: ObjectSKInput): string {
		switch (index) {
			case 2:
			default:
				return `SESSION#${key.paymentSessionId}#ORDERITEM#${key.orderItemId ? key.orderItemId : ""}`;
		}
	}
	getGlobalIndices(): object {
		return {
			GSI2PK: this.generateGSIPK(2, this),
			GSI2SK: this.generateGSISK(2, this),
		};
	}
	getColumnsForExport(): string[] {
		throw new Error("Method not implemented.");
	}
	getObjectForExport(): object {
		throw new Error("Method not implemented.");
	}

	get orderId(): string {
		return this.checkFieldType<"orderId">(this._orderId);
	}
	set orderId(value: string) {
		this._orderId = this.checkFieldTypeAndMark<"orderId">("_orderId", value);
	}

	get orderItemId(): string {
		return this.checkFieldType<"orderItemId">(this._orderItemId);
	}
	set orderItemId(value: string) {
		this._orderItemId = this.checkFieldTypeAndMark<"orderItemId">("_orderItemId", value);
	}

	get exteriorKey(): string | undefined {
		return this.checkFieldType<"exteriorKey">(this._exteriorKey);
	}
	set exteriorKey(value: string | undefined) {
		this._exteriorKey = this.checkFieldTypeAndMark<"exteriorKey">("_exteriorKey", value);
	}

	get interiorKey(): string | undefined {
		return this.checkFieldType<"interiorKey">(this._interiorKey);
	}
	set interiorKey(value: string | undefined) {
		this._interiorKey = this.checkFieldTypeAndMark<"interiorKey">("_interiorKey", value);
	}
	public getPackageKey(type: "interior" | "exterior"): string {
		if (type === "interior") {
			if (!this.interiorKey) throw new BasicError("Key not present", "Key not present for type: " + type);
			return this.interiorKey;
		}
		if (type === "exterior") {
			if (!this.exteriorKey) throw new BasicError("Key not present", "Key not present for type: " + type);
			return this.exteriorKey;
		}
		throw new BasicError("Type invalid", "Type invalid: " + type);
	}
}
